<template>
  <div class="link-waiver-modal">
    <SDialog v-model:open="dialogVisible">
      <SDialogContent
        class="link-waiver-modal !w-[60%] !max-w-[60%] !top-[8vh] !translate-y-0"
        :show-close-button="false"
        @escape-key-down.prevent
        @pointer-down-outside.prevent
        @open-auto-focus.prevent
      >
        <!-- Header -->
        <SDialogHeader>
          <div class="modal-header d-flex fl-row fl-a-c fl-s-b">
            <div class="d-flex fl-row fl-a-c">
              <SvgIcon
                :iconContent="waiverIcon"
                iconAlt="waiver"
                iconStyle="width: 20px; height: 20px; margin-right: 8px;"
              />
              <SDialogTitle
                class="modal-title"
                data-cy="link-waiver-modal-title"
              >
                {{ $t("waiver.link-a-waiver") }}
              </SDialogTitle>
            </div>
            <div>
              <span
                class="close-button"
                @click="close"
                data-cy="link-waiver-modal-close"
              >
                {{ $t("button.close") }}
              </span>
            </div>
          </div>
        </SDialogHeader>

        <NoAccessEmpty v-if="!getHasAccess" />

        <template v-else>
          <!-- Search -->
          <div class="search-wrapper">
            <p class="search-label">{{ $t("waiver.search-for-a-waiver") }}</p>
            <SInputGroup>
              <SInputGroupAddon align="inline-start">
                <SearchIcon />
              </SInputGroupAddon>
              <SInputGroupInput
                :model-value="searchQuery"
                :placeholder="$t('waiver.search-placeholder')"
                @update:model-value="
                  (v) => {
                    searchQuery = String(v);
                    onSearch();
                  }
                "
                data-cy="waiver-search-input"
              />
              <SInputGroupAddon v-if="searchQuery" align="inline-end">
                <SInputGroupButton
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label="Clear search"
                  @click="
                    () => {
                      searchQuery = '';
                      onSearch();
                    }
                  "
                >
                  <XIcon />
                </SInputGroupButton>
              </SInputGroupAddon>
            </SInputGroup>
          </div>

          <!-- Table -->
          <div class="table-wrapper">
            <p class="table-title">{{ $t("waiver.signed-waivers") }}</p>
            <el-table
              :data="filteredWaivers"
              style="width: 100%"
              :row-class-name="getRowClass"
              data-cy="waivers-table"
            >
              <!-- Waiver ID -->
              <el-table-column
                prop="waiverId"
                :label="$t('waiver.waiver-id')"
                sortable
              >
                <template #header>
                  <span>{{ $t("waiver.waiver-id") }}</span>
                  <el-icon class="sort-icon"><Sort /></el-icon>
                </template>
              </el-table-column>

              <!-- Waiver title -->
              <el-table-column
                prop="waiverTitle"
                :label="$t('waiver.waiver-title')"
                sortable
              >
                <template #header>
                  <span>{{ $t("waiver.waiver-title") }}</span>
                  <el-icon class="sort-icon"><Sort /></el-icon>
                </template>
              </el-table-column>

              <!-- Customer name -->
              <el-table-column
                prop="customerName"
                :label="$t('waiver.customer-name')"
                sortable
              >
                <template #header>
                  <span>{{ $t("waiver.customer-name") }}</span>
                  <el-icon class="sort-icon"><Sort /></el-icon>
                </template>
              </el-table-column>

              <!-- N° of Dependents -->
              <el-table-column
                prop="numberOfDependents"
                :label="$t('waiver.number-of-dependents')"
                sortable
              >
                <template #header>
                  <span>{{ $t("waiver.number-of-dependents") }}</span>
                  <el-icon class="sort-icon"><Sort /></el-icon>
                </template>
                <template #default="{ row }">
                  <span>{{ row.numberOfDependents ?? "-" }}</span>
                </template>
              </el-table-column>

              <!-- Signed at -->
              <el-table-column
                prop="signedAt"
                :label="$t('waiver.signed-at')"
                sortable
              >
                <template #header>
                  <span>{{ $t("waiver.signed-at") }}</span>
                  <el-icon class="sort-icon"><Sort /></el-icon>
                </template>
              </el-table-column>

              <!-- Link / Unlink action -->
              <el-table-column width="110" align="right">
                <template #default="{ row }">
                  <button
                    v-if="isLinked(row.waiverId)"
                    class="action-btn unlink-btn"
                    :disabled="loadingWaiverId === row.waiverId"
                    @click="onUnlink(row)"
                    :data-cy="`unlink-waiver-${row.waiverId}`"
                  >
                    <el-icon v-if="loadingWaiverId === row.waiverId">
                      <Loading />
                    </el-icon>
                    <template v-else>
                      {{ $t("waiver.unlink") }}
                      <el-icon><CircleClose /></el-icon>
                    </template>
                  </button>
                  <button
                    v-else
                    class="action-btn link-btn"
                    :disabled="loadingWaiverId === row.waiverId"
                    @click="onLink(row)"
                    :data-cy="`link-waiver-${row.waiverId}`"
                  >
                    <el-icon v-if="loadingWaiverId === row.waiverId">
                      <Loading />
                    </el-icon>
                    <template v-else>
                      {{ $t("waiver.link") }}
                      <SvgIcon
                        :iconContent="linkIcon"
                        iconAlt="link"
                        iconStyle="width: 13px; height: 13px; margin-left: 4px;"
                      />
                    </template>
                  </button>
                </template>
              </el-table-column>
            </el-table>

            <!-- Empty state -->
            <div
              v-if="!filteredWaivers.length && !isLoading"
              class="empty-state"
              data-cy="waivers-empty-state"
            >
              <p>{{ $t("waiver.no-waivers-found") }}</p>
            </div>

            <!-- Loading state -->
            <div
              v-if="isLoading"
              class="loading-state"
              data-cy="waivers-loading"
            >
              <el-icon class="is-loading"><Loading /></el-icon>
            </div>

            <!-- Load more -->
            <div v-if="hasMore && !isLoading" class="load-more-wrapper">
              <SButton
                type="button"
                variant="outline"
                class="load-more-btn"
                :loading="isLoadingMore"
                @click="loadMore"
                data-cy="load-more-waivers-btn"
              >
                {{ $t("waiver.load-more") }}
              </SButton>
            </div>
          </div>
        </template>
      </SDialogContent>
    </SDialog>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, inject, ref, watch } from "vue";
import { SvgIcon } from "@/components/index";
import NoAccessEmpty from "@/components/common/NoAccessEmpty.vue";
import UserModule from "@/store/modules/user";
import { EngineKeys } from "@/app-search/services/request-app-search.service";
import { Sort, CircleClose, Loading } from "@element-plus/icons-vue";
import { dialogVisibleForStatus } from "@/utils/vue-helpers/modal";
import { ModalStatus } from "@/constants/modal";
import { ISignedWaiver } from "@/interfaces/waivers";
import {
  getSignedWaivers,
  linkWaiverToOrder,
  unlinkWaiverFromOrder,
  WaiverErrorReason,
} from "@/api/waivers/waivers";
import { getServicesErrorReason } from "@/utils/helpers/not-synced-retry";
import { logOrderActionError } from "@/utils/vue-helpers/order-action-errors.helper";
import NotificationService from "@/services/notification.service";
import { LogLevel, LogService } from "@/services/logging";
import { useOrderStore } from "@smeetz/order-store";
import { useI18n } from "vue-i18n";
import waiverIcon from "@/assets/images/svg/link.svg";
import linkIcon from "@/assets/images/svg/qrIcon.svg";
import { LINKED_WAIVERS_KEY } from "@/components/layout/aside-order-summary/waiver-keys";
import {
  SButton,
  SDialog,
  SDialogContent,
  SDialogHeader,
  SDialogTitle,
  SInputGroup,
  SInputGroupAddon,
  SInputGroupButton,
  SInputGroupInput,
} from "@smeetz/ui-core";
import { SearchIcon, XIcon } from "@smeetz/ui-icons";

export default defineComponent({
  name: "LinkWaiverModal",
  components: {
    SvgIcon,
    NoAccessEmpty,
    Sort,
    CircleClose,
    Loading,
    SButton,
    SDialog,
    SDialogContent,
    SDialogHeader,
    SDialogTitle,
    SInputGroup,
    SInputGroupAddon,
    SInputGroupButton,
    SInputGroupInput,
    SearchIcon,
    XIcon,
  },
  setup() {
    const { t } = useI18n({});
    const dialogVisible = dialogVisibleForStatus(ModalStatus.LinkWaiver);
    const linkedWaivers = inject(LINKED_WAIVERS_KEY);
    const orderStore = useOrderStore();

    const searchQuery = ref("");
    const allWaivers = ref<ISignedWaiver[]>([]);
    const isLoading = ref(false);
    const isLoadingMore = ref(false);
    const loadingWaiverId = ref<string | null>(null);
    const currentFrom = ref(0);
    const totalWaivers = ref(0);
    let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

    // ── Computed ──────────────────────────────────────────────────────────────

    const linkedWaiverIds = computed(() => {
      return linkedWaivers?.linkedWaiverIds.value ?? [];
    });

    const hasMore = computed(
      () => allWaivers.value.length < totalWaivers.value
    );

    // No longer client-side filtered — results come directly from ES
    const filteredWaivers = computed(() => allWaivers.value);

    // The waiver engine is granted to manager-level roles only. A seller gets a
    // null token, which the search would render as an empty list.
    const getHasAccess = computed(
      () => !!UserModule.getUser?.elasticSearchTokens?.[EngineKeys.Waiver]
    );

    // ── Helpers ───────────────────────────────────────────────────────────────

    function isLinked(waiverId: string) {
      return linkedWaiverIds.value.includes(waiverId);
    }

    function getRowClass({ row }: { row: ISignedWaiver }) {
      return isLinked(row.waiverId) ? "linked-row" : "";
    }

    // ── Data fetching ─────────────────────────────────────────────────────────

    async function fetchWaivers(query?: string) {
      isLoading.value = true;
      currentFrom.value = 0;
      try {
        const { waivers, total } = await getSignedWaivers(query, 0);
        allWaivers.value = waivers;
        totalWaivers.value = total;
        currentFrom.value = waivers.length;
      } catch (e) {
        NotificationService.showGeneralResponseError();
      } finally {
        isLoading.value = false;
      }
    }

    async function loadMore() {
      isLoadingMore.value = true;
      try {
        const { waivers } = await getSignedWaivers(
          searchQuery.value || undefined,
          currentFrom.value
        );
        allWaivers.value = [...allWaivers.value, ...waivers];
        currentFrom.value += waivers.length;
      } catch (e) {
        NotificationService.showGeneralResponseError();
      } finally {
        isLoadingMore.value = false;
      }
    }

    // Fetch when modal opens, and re-read the order's links so a waiver linked
    // from another till shows as linked.
    watch(dialogVisible, (val) => {
      if (val) {
        searchQuery.value = "";
        fetchWaivers();
        linkedWaivers?.refresh();
      }
    });

    // ── Search ────────────────────────────────────────────────────────────────

    function onSearch() {
      if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
      searchDebounceTimer = setTimeout(() => {
        fetchWaivers(searchQuery.value || undefined);
      }, 300);
    }

    // ── Link / Unlink ─────────────────────────────────────────────────────────

    function waiverActionErrorMessage(reason: string | undefined): string {
      if (reason === WaiverErrorReason.GroupMismatch) {
        return t("waiver.cross-group-link");
      }
      if (reason === WaiverErrorReason.OrderNotSynced) {
        return t("waiver.order-not-synced");
      }
      return "";
    }

    /**
     * Run a link or unlink on the selected order, then re-read its links so the
     * row state and the aside count come from the backend. Never throws: a
     * failure is journaled and toasted, and nothing is re-read since nothing
     * changed.
     *
     * @param action gerund for the log line, e.g. "linking waiver"
     */
    async function runWaiverAction(
      waiver: ISignedWaiver,
      action: string,
      call: (waiverId: string, orderId: string) => Promise<void>
    ): Promise<void> {
      const orderId = orderStore.selectedOrder?.id;
      const orderReference = orderStore.selectedOrder?.reference;
      const context = { orderId, waiverId: waiver.waiverId };
      if (!orderId) {
        LogService.info(
          `Error ${action}: no selected order`,
          context,
          LogLevel.Error
        );
        NotificationService.showGeneralResponseError();
        return;
      }

      loadingWaiverId.value = waiver.waiverId;
      try {
        await call(waiver.waiverId, orderId);
        // The order is synced once the call succeeded, so one read is enough.
        await linkedWaivers?.refresh(0);
      } catch (error) {
        const reason = getServicesErrorReason(error);
        const message = waiverActionErrorMessage(reason);
        if (message) {
          LogService.info(
            `Warning ${action} on order ${orderReference}: ${reason}`,
            context,
            LogLevel.Warning
          );
          NotificationService.showCustomError(message);
        } else {
          logOrderActionError(
            `${action} on order ${orderReference}`,
            context,
            error
          );
          NotificationService.showGeneralResponseError();
        }
      } finally {
        loadingWaiverId.value = null;
      }
    }

    function onLink(waiver: ISignedWaiver) {
      return runWaiverAction(waiver, "linking waiver", linkWaiverToOrder);
    }

    function onUnlink(waiver: ISignedWaiver) {
      return runWaiverAction(waiver, "unlinking waiver", unlinkWaiverFromOrder);
    }

    function close() {
      dialogVisible.value = false;
    }

    return {
      dialogVisible,
      searchQuery,
      filteredWaivers,
      getHasAccess,
      isLoading,
      loadingWaiverId,
      isLinked,
      getRowClass,
      onSearch,
      onLink,
      onUnlink,
      close,
      Sort,
      waiverIcon,
      linkIcon,
      hasMore,
      isLoadingMore,
      loadMore,
    };
  },
});
</script>

<style lang="scss">
@use "@/styles/variables.scss" as *;

.link-waiver-modal {
  .modal-header {
    padding: 4px 0;

    .modal-title {
      font-size: 16px;
      font-weight: 700;
      color: $color-text-primary;
    }

    .close-button {
      font-size: 14px;
      color: $color-dark-cyan;
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .search-wrapper {
    margin-bottom: 20px;

    .search-label {
      font-size: 13px;
      font-weight: 600;
      margin-bottom: 6px;
      color: $color-text-primary;
    }
  }

  .table-title {
    font-size: 14px;
    font-weight: 700;
    margin-bottom: 10px;
    color: $color-text-primary;
  }

  // Linked row highlight — teal tint matching the design
  .el-table .linked-row {
    background-color: rgba(75, 155, 165, 0.12);
  }

  .action-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border: none;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.2s;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .link-btn {
    color: $color-dark-cyan;
    background: transparent;

    &:hover:not(:disabled) {
      text-decoration: underline;
    }
  }

  .unlink-btn {
    color: $color-danger-red;
    background: transparent;

    &:hover:not(:disabled) {
      text-decoration: underline;
    }
  }

  .empty-state,
  .loading-state {
    text-align: center;
    padding: 24px 0;
    color: $color-light-grey2;
    font-size: 13px;
  }

  .loading-state .el-icon {
    font-size: 24px;
  }

  .sort-icon {
    margin-left: 4px;
    vertical-align: middle;
  }

  .load-more-wrapper {
    display: flex;
    justify-content: center;
    padding: 12px 0;

    .load-more-btn {
      color: $color-dark-cyan;
      border-color: $color-dark-cyan;
      font-weight: 600;
    }
  }

  .table-wrapper {
    margin-top: 8px;
  }
}
</style>
